/* Professional ringtone player — uses pre-rendered WAV files (richer +
   warmer than oscillator beeps) instead of synthesised Web Audio tones.

   Why <audio> and not AudioBufferSource:
   - <audio loop> is one line of code.
   - On iOS Safari/PWA <audio> respects the page's AudioSession routing
     (earpiece vs loudspeaker) more predictably than a raw
     AudioBufferSourceNode.
   - The asset is ~250 KB and is cached after first call.

   iOS PWA constraints:
   - .play() must be called from a user gesture, OR the page must have
     received any prior user gesture in the session.
   - We expose `unlockAudio()` which the caller invokes from the
     "Call" / "Accept" button onClick (those are gestures), priming both
     <audio> elements so subsequent reuses play without further taps.
*/

const SRC = {
  incoming: "/sounds/ringtone.wav",
  ringback: "/sounds/ringback.wav",
};

const elements = {};
let active = null;

function getEl(kind) {
  if (elements[kind]) return elements[kind];
  const el = new Audio(SRC[kind]);
  el.loop = true;
  el.preload = "auto";
  el.playsInline = true;
  el.setAttribute("playsinline", "");
  el.setAttribute("webkit-playsinline", "");
  el.volume = kind === "incoming" ? 1.0 : 0.85;
  elements[kind] = el;
  return el;
}

/* Public: prime audio elements with a silent .play()/.pause() pair. Called
   from inside a user-gesture handler (tap on Call / Accept button) so iOS
   "unlocks" both sounds for the rest of the session. */
export function unlockAudio() {
  try {
    const a = getEl("incoming");
    const b = getEl("ringback");
    // Mute, play, immediately pause — counts as a "user-initiated" play
    // attempt on iOS without producing any audible click.
    [a, b].forEach((el) => {
      el.muted = true;
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          el.pause();
          el.currentTime = 0;
          el.muted = false;
        }).catch(() => {
          el.muted = false;
        });
      } else {
        el.pause();
        el.muted = false;
      }
    });
  } catch {
    /* noop */
  }
}

/* Public: start the ring of the given kind. Stops whatever is currently
   playing first so the two never overlap. */
export function playRing(kind = "incoming") {
  stopRing();
  const el = getEl(kind);
  el.muted = false;
  el.currentTime = 0;
  active = kind;
  const p = el.play();
  if (p && typeof p.catch === "function") {
    p.catch(() => {
      /* iOS may reject if there has been NO gesture at all in the session.
         The unlockAudio() call from Call/Accept buttons normally prevents
         this. Silently swallow so the call still proceeds without ring. */
    });
  }
}

export function stopRing() {
  if (active && elements[active]) {
    try {
      elements[active].pause();
      elements[active].currentTime = 0;
    } catch { /* noop */ }
  }
  active = null;
}
